/**
 * Análise de oportunidades do motor por imóvel (`/analysis/.../opportunities`,
 * só admin) — compartilhada pela lista de Oportunidades e pelo card
 * "Economize até" do Dashboard (out/2026).
 *
 * Cada análise passa várias faturas pelo motor: medido em 27/out/2026 no banco
 * local, 1,2–1,8 s por imóvel (6 imóveis ≈ 8,7 s em série). Por isso: no
 * máximo 2 por vez, e o resultado fica em memória por 10 minutos — ir do
 * Dashboard para Oportunidades (ou voltar) não recalcula. Falha não fica no
 * cache (tenta de novo na próxima vez).
 */
import { useEffect, useState } from 'react';

import {
  getOpportunitiesService,
  OpportunitiesResult,
} from 'services/analysis.service';

export interface OpportunityAnalysis {
  loading: boolean;
  failed?: boolean;
  result?: OpportunitiesResult;
}

const CONCURRENCY = 2;
const TTL_MS = 10 * 60 * 1000;

const cache = new Map<
  string,
  { at: number; promise: Promise<OpportunitiesResult | null> }
>();
const waiting: (() => void)[] = [];
let running = 0;

const next = () => {
  while (running < CONCURRENCY && waiting.length) {
    const job = waiting.shift();
    if (job) job();
  }
};

const analyse = (id: string): Promise<OpportunitiesResult | null> => {
  const hit = cache.get(id);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;

  const promise = new Promise<OpportunitiesResult | null>((resolve) => {
    waiting.push(async () => {
      running += 1;
      try {
        const response = await getOpportunitiesService(id);
        resolve(response.data.data);
      } catch {
        cache.delete(id);
        resolve(null);
      } finally {
        running -= 1;
        next();
      }
    });
    next();
  });
  cache.set(id, { at: Date.now(), promise });
  return promise;
};

/** Estado da análise de cada imóvel; `enabled = false` não dispara nada. */
export const useOpportunityAnalyses = (
  consumerUnitIds: string[],
  enabled = true
): Record<string, OpportunityAnalysis> => {
  const [analyses, setAnalyses] = useState<Record<string, OpportunityAnalysis>>(
    {}
  );
  const key = consumerUnitIds.join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setAnalyses(
      Object.fromEntries(consumerUnitIds.map((id) => [id, { loading: true }]))
    );
    consumerUnitIds.forEach((id) => {
      analyse(id).then((result) => {
        if (cancelled) return;
        setAnalyses((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            failed: result === null,
            result: result ?? undefined,
          },
        }));
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a lista é a chave
  }, [key, enabled]);

  return analyses;
};
